import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Spinner } from "@/components/ui/Spinner";
import { Badge } from "@/components/ui/Badge";
import { getRoleBadgeColor, getRoleLabel } from "@/utils/roleLabel";
import apiClient from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { formatPhone } from "@/utils/formatPhone";

interface UserEntry {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  role: string;
  is_active: boolean;
  is_developer: boolean;
  is_ceo: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  director: "Dyrektor",
  coordinator: "Koordynator",
  accountant: "Księgowy",
  employee: "Pracownik",
  hr: "HR",
  contractor: "Podwykonawca",
};

function useUsers() {
  return useQuery<UserEntry[]>({
    queryKey: ["admin-users-list"],
    queryFn: () =>
      apiClient
        .get<{ data: UserEntry[] }>("/auth/users/")
        .then((r) => r.data.data),
    staleTime: 1000 * 60 * 2,
  });
}

function ContactModal({
  user,
  onClose,
}: {
  user: UserEntry;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const parseLocalPhone = (full: string) => {
    const digits = full.replace(/\D/g, "");
    if (digits.startsWith("48") && digits.length > 9) return digits.slice(2);
    return digits.slice(-9);
  };
  const [phone, setPhone] = useState(() => parseLocalPhone(user.phone_number ?? ""));
  const [email, setEmail] = useState(user.email ?? "");
  const [error, setError] = useState("");

  const mut = useMutation({
    mutationFn: (data: { phone_number?: string; email?: string }) =>
      apiClient.patch(`/auth/users/${user.id}/set-contact/`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-users-list"] });
      onClose();
    },
    onError: () => setError("Błąd podczas zapisywania danych."),
  });

  const handleSave = () => {
    setError("");
    mut.mutate({ phone_number: phone ? `+48${phone}` : "", email });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm p-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
          Dane kontaktowe — {user.first_name} {user.last_name || user.username}
        </h2>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Telefon
            </label>
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 focus-within:ring-2 focus-within:ring-[var(--color-primary)] focus-within:border-[var(--color-primary)] overflow-hidden bg-white dark:bg-gray-800">
              <div className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 dark:bg-gray-700 border-r border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 select-none whitespace-nowrap">
                <span>🇵🇱</span>
                <span>+48</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 9))}
                placeholder="000 000 000"
                maxLength={9}
                className="flex-1 min-w-0 px-3 py-2 text-sm focus:outline-none bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              E-mail
            </label>
            <input
              type="email"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="adres@example.com"
            />
          </div>
        </div>
        {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
        <div className="flex gap-2 justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Anuluj
          </button>
          <button
            onClick={handleSave}
            disabled={mut.isPending}
            className="px-4 py-2 text-sm rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white disabled:opacity-50"
          >
            {mut.isPending ? "Zapisywanie…" : "Zapisz"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PasswordModal({
  user,
  onClose,
}: {
  user: UserEntry;
  onClose: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const mut = useMutation({
    mutationFn: (pwd: string) =>
      apiClient.post(`/auth/users/${user.id}/reset-password/`, { password: pwd }),
    onSuccess: () => setSuccess(true),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { errors?: { password?: string } } } })
          ?.response?.data?.errors?.password ?? "Błąd podczas zmiany hasła.";
      setError(msg);
    },
  });

  const handleSave = () => {
    setError("");
    if (password.length < 8) {
      setError("Hasło musi mieć co najmniej 8 znaków.");
      return;
    }
    if (password !== confirm) {
      setError("Hasła nie są zgodne.");
      return;
    }
    mut.mutate(password);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm p-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
          Reset hasła — {user.first_name} {user.last_name || user.username}
        </h2>
        {success ? (
          <div className="text-sm text-green-600 dark:text-green-400 mb-4">
            Hasło zostało zmienione.
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Nowe hasło
              </label>
              <input
                type="password"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="min. 8 znaków"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Powtórz hasło
              </label>
              <input
                type="password"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
          </div>
        )}
        {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
        <div className="flex gap-2 justify-end mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {success ? "Zamknij" : "Anuluj"}
          </button>
          {!success && (
            <button
              onClick={handleSave}
              disabled={mut.isPending}
              className="px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {mut.isPending ? "Zapisywanie…" : "Zmień hasło"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function DevAccountsPage() {
  const user = useAuthStore((s) => s.user);
  const isDevOrDirector = !!(user?.is_developer || user?.role === "director");
  const isCeo = !!user?.is_ceo;
  const canResetPasswordFor = (target: UserEntry) =>
    isCeo || target.role !== "director";
  const { data: users = [], isLoading } = useUsers();
  const [search, setSearch] = useState("");
  const [contactTarget, setContactTarget] = useState<UserEntry | null>(null);
  const [passwordTarget, setPasswordTarget] = useState<UserEntry | null>(null);

  if (!isDevOrDirector) {
    return (
      <div className="p-6 text-sm text-red-500">Brak dostępu.</div>
    );
  }

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return (
      u.username.toLowerCase().includes(q) ||
      u.first_name.toLowerCase().includes(q) ||
      u.last_name.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-5 pb-3 md:px-6 md:pt-6 md:pb-4">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white md:text-2xl">Konta użytkowników</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Reset hasła i dane kontaktowe
        </p>
      </div>

      <div className="px-4 pb-3 md:px-6 md:pb-4">
        <input
          className="w-full md:max-w-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          placeholder="Szukaj po nazwie, e-mailu…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-6 md:px-6">
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : (
          <>
            {/* Mobile: card list */}
            <div className="md:hidden space-y-2">
              {filtered.length === 0 && (
                <p className="py-8 text-center text-gray-400 text-sm">Brak wyników</p>
              )}
              {filtered.map((u) => (
                <div
                  key={u.id}
                  className={`bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-700 p-4 ${!u.is_active ? "opacity-50" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                        {u.first_name} {u.last_name}
                        {!u.is_active && (
                          <span className="ml-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded px-1">
                            nieaktywny
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 truncate">{u.username}</div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                      <Badge color={getRoleBadgeColor(u.role, u.is_ceo)}>
                        {getRoleLabel(u.role, u.is_ceo)}
                      </Badge>
                      {u.is_developer && (
                        <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded px-1 inline-flex items-center gap-0.5">
                          <span>💻🔥</span>
                          dev
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5 mb-3">
                    {u.phone_number && (
                      <div>{formatPhone(u.phone_number)}</div>
                    )}
                    {u.email && <div className="truncate">{u.email}</div>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setContactTarget(u)}
                      className="flex-1 text-xs py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Dane kontaktowe
                    </button>
                    {canResetPasswordFor(u) && (
                      <button
                        onClick={() => setPasswordTarget(u)}
                        className="flex-1 text-xs py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                      >
                        Reset hasła
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block bg-white dark:bg-gray-800 overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-gray-50 dark:bg-gray-800 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Użytkownik</th>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Rola</th>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Telefon</th>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">E-mail</th>
                    <th className="px-4 py-2 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Akcje</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filtered.map((u) => (
                    <tr
                      key={u.id}
                      className={`bg-white dark:bg-gray-900 ${!u.is_active ? "opacity-50" : ""}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {u.first_name} {u.last_name}
                          {!u.is_active && (
                            <span className="ml-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded px-1">
                              nieaktywny
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">{u.username}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge color={getRoleBadgeColor(u.role, u.is_ceo)}>
                            {getRoleLabel(u.role, u.is_ceo)}
                          </Badge>
                          {u.is_developer && (
                            <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded px-1 inline-flex items-center gap-0.5">
                              <span>💻🔥</span>
                              dev
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {formatPhone(u.phone_number)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {u.email || <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => setContactTarget(u)}
                            className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            Dane kontaktowe
                          </button>
                          {canResetPasswordFor(u) && (
                            <button
                              onClick={() => setPasswordTarget(u)}
                              className="text-xs px-2.5 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                            >
                              Reset hasła
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">
                        Brak wyników
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {contactTarget && (
        <ContactModal user={contactTarget} onClose={() => setContactTarget(null)} />
      )}
      {passwordTarget && (
        <PasswordModal user={passwordTarget} onClose={() => setPasswordTarget(null)} />
      )}
    </div>
  );
}
